import type { IncomingHttpHeaders } from 'http';
import type { AxiosResponse } from 'axios';
import getConfig from 'next/config';

import { donedealServicesGateway } from 'api/api-utils/commonApiUtils';
import type {
  IGetAdsC2BRequest,
  IGetAdsC2BResponse,
  IGetAdsRequest,
  IGetFiltersResponse,
} from 'api/types/searchPageApiTypes';
import {
  IGetAdsResponse,
  IGetSearchFilterMakeModel,
  IGetSection,
} from 'api/types/searchPageApiTypes';
import { ENVIRONMENT_NAME } from 'utils';
import axios from 'axios';

const {
  publicRuntimeConfig: { API_LISTINGS_TIMEOUT },
} = getConfig();

async function getAds(
  payload: IGetAdsRequest,
  headers: IncomingHttpHeaders = { accept: 'application/json' },
  timeout?: number,
): Promise<AxiosResponse<IGetAdsResponse>> {
  const response: AxiosResponse<IGetAdsResponse> =
    await donedealServicesGateway.post(`/ddapi/v1/search`, payload, {
      headers: {
        ...headers,
        accept: 'application/json',
      },
      timeout: timeout || parseInt(API_LISTINGS_TIMEOUT),
    });

  return response;
}

async function getAdsC2B(
  payload: IGetAdsC2BRequest,
  headers: IncomingHttpHeaders = { accept: 'application/json' },
  timeout?: number,
): Promise<AxiosResponse<IGetAdsC2BResponse>> {
  if (ENVIRONMENT_NAME === 'dev') {
    const response: AxiosResponse<IGetAdsC2BResponse> = await axios.post(
      'https://dd-dealer-agg.donedeal.dev/v1/search/c2b',
      payload,
      {
        headers: {
          ...headers,
          accept: 'application/json',
          'X-Identity-UserId': 30049,
        },
        timeout: timeout || parseInt(API_LISTINGS_TIMEOUT),
      },
    );
    return response;
  } else {
    const response: AxiosResponse<IGetAdsC2BResponse> =
      await donedealServicesGateway.post('dhapi/v1/search/c2b', payload, {
        headers: {
          ...headers,
          accept: 'application/json',
        },
        timeout: timeout || parseInt(API_LISTINGS_TIMEOUT),
      });
    return response;
  }
}

async function getFilters(
  section: string,
  headers: IncomingHttpHeaders = { accept: 'application/json' },
  dealerId: string | null = null,
  timeout: number = parseInt(API_LISTINGS_TIMEOUT),
): Promise<AxiosResponse<IGetFiltersResponse>> {
  const response: AxiosResponse<IGetFiltersResponse> =
    await donedealServicesGateway.get(
      dealerId
        ? `/ddapi/v1/search/showroom/filters/${section}?dealerId=${dealerId}`
        : `/ddapi/v2/search/filters/${section}`,
      {
        headers: {
          ...headers,
          accept: 'application/json',
        },
        timeout,
      },
    );
  return response;
}

async function getFiltersC2B(
  headers: IncomingHttpHeaders = { accept: 'application/json' },
): Promise<AxiosResponse<IGetFiltersResponse>> {
  const response: AxiosResponse<IGetFiltersResponse> =
    await donedealServicesGateway.get('ddapi/v1/search/filters/cars-c2b', {
      headers: {
        ...headers,
        accept: 'application/json',
      },
      timeout: parseInt(API_LISTINGS_TIMEOUT),
    });
  return response;
}

async function getModelByMake(
  section: string,
  make: string,
  headers: IncomingHttpHeaders = { accept: 'application/json' },
  dealerId: string | null = null,
  timeout: number = parseInt(API_LISTINGS_TIMEOUT),
): Promise<AxiosResponse<IGetSearchFilterMakeModel>> {
  const response: AxiosResponse<IGetSearchFilterMakeModel> =
    await donedealServicesGateway.get(
      dealerId
        ? `/ddapi/v1/search/showroom/filters/${section}/${make}?dealerId=${dealerId}`
        : `/ddapi/v1/search/filters/${section}/${make}`,
      {
        headers: {
          ...headers,
          accept: 'application/json',
        },
        timeout,
      },
    );
  return response;
}

async function getSections(
  section: string = 'all',
  payload: IGetAdsRequest,
  headers: IncomingHttpHeaders = { accept: 'application/json' },
  timeout: number = parseInt(API_LISTINGS_TIMEOUT),
): Promise<AxiosResponse<IGetSection>> {
  const response: AxiosResponse<IGetSection> =
    await donedealServicesGateway.post(
      `/ddapi/v1/search/sections/${section}`,
      payload,
      {
        headers: {
          ...headers,
          accept: 'application/json',
        },
        timeout,
      },
    );

  return response;
}

export const searchPageApi = {
  getAds,
  getAdsC2B,
  getFilters,
  getFiltersC2B,
  getModelByMake,
  getSections,
};
